import React from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import LegacyButton from '@atlaskit/button/standard-button';
import ChevronLeftIcon from '@atlaskit/icon/core/migration/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/core/migration/chevron-right';
import { Box, xcss } from '@atlaskit/primitives';
import Select from '@atlaskit/select';
import Tooltip from '@atlaskit/tooltip';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import {
	GET_EXTRA_SELECT_PROPS,
	getNavigationLabels,
	getScheduleViewOptions,
	getTimelineTypeOptions,
	NavigationMode,
	type NavigationPeriodId,
	commonMessages as scheduleCommonMessages,
	useResize,
} from '@atlassian/eoc-schedule-common';
import { Skeleton } from '@atlassian/eoc-skeleton';

import { NowLocation } from '../../../utils/constants/schedule';
import { getNowLocation } from '../../../utils/schedule';

import { periodSelectStyles, RangeContainer, ScheduleDate, viewSelectStyles } from './styled';
import { type DateNavigationProps } from './types';
import { formatScheduleDate, timelineTypeToName } from './utils';

const arrowButtonContainerStyles = xcss({
	marginRight: 'space.100',
});

const DateNavigation: React.FC<DateNavigationProps> = ({
	startEpoch,
	endEpoch,
	nowEpoch,
	startOffset,
	endOffset,
	isLoading,
	nextPeriod,
	onTimelineTypeChange,
	onToday,
	onViewChange,
	previousPeriod,
	selectedTimelineType,
	selectedView,
	scheduleName,
}) => {
	const intlApi = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [bodyWidth] = useResize(document.documentElement);

	const navLabels = getNavigationLabels(intlApi)(selectedTimelineType, scheduleName);

	return (
		<>
			<RangeContainer>
				{isLoading ? (
					<Skeleton height={2} width={15} />
				) : (
					<>
						{getNowLocation(startEpoch, endEpoch, nowEpoch) !== NowLocation.InRange && (
							<LegacyButton
								// TODO: (from codemod) "link" and "subtle-link" appearances are only available in LinkButton, please either provide a href prop then migrate to LinkButton, or remove the appearance from the default button.
								appearance="link"
								// TODO: prevent change if loading
								onClick={() => onToday()}
							>
								{intlApi.formatMessage(scheduleCommonMessages.today)}
							</LegacyButton>
						)}
						<ScheduleDate>
							{formatScheduleDate({
								intlApi,
								startEpoch,
								endEpoch,
								startOffset,
								endOffset,
								view: selectedView,
								range: selectedTimelineType,
								variant: bodyWidth! <= 1024 ? 'short' : 'long',
							})}
						</ScheduleDate>
					</>
				)}
			</RangeContainer>

			<Tooltip content={navLabels.previous.tooltip} position="bottom">
				<Box xcss={arrowButtonContainerStyles}>
					<IconButton
						// TODO: prevent change if loading
						onClick={previousPeriod}
						label={navLabels.previous.aria}
						icon={(iconProps) => <ChevronLeftIcon {...iconProps} size="small" />}
					/>
				</Box>
			</Tooltip>
			<Tooltip content={navLabels.next.tooltip} position="bottom">
				<Box xcss={arrowButtonContainerStyles}>
					<IconButton
						// TODO: prevent change if loading
						onClick={nextPeriod}
						label={navLabels.next.aria}
						icon={(iconProps) => <ChevronRightIcon {...iconProps} size="small" />}
					/>
				</Box>
			</Tooltip>

			{selectedView === NavigationMode.Timeline && (
				<Select<{ label: string; value: NavigationPeriodId }>
					{...GET_EXTRA_SELECT_PROPS()}
					isSearchable={false}
					// TODO: prevent change if loading
					onChange={(option) => {
						const event = createAnalyticsEvent({});
						fireUIAnalytics(event, 'button clicked', `${timelineTypeToName(option!.value)}`);

						onTimelineTypeChange(option!.value);
					}}
					options={getTimelineTypeOptions(intlApi).map((timelineTypeOption) => timelineTypeOption)}
					styles={{
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						control: (base) => ({ ...base, ...periodSelectStyles }),
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						...GET_EXTRA_SELECT_PROPS().styles,
					}}
					spacing="compact"
					value={getTimelineTypeOptions(intlApi).find(
						({ value }) => value === selectedTimelineType,
					)}
					label={intlApi.formatMessage(scheduleCommonMessages.selectTimelineView, {
						scheduleName,
					})}
					placeholder={intlApi.formatMessage(scheduleCommonMessages.selectTimelineView, {
						scheduleName,
					})}
				/>
			)}

			<Select<{ label: string; value: NavigationMode }>
				{...GET_EXTRA_SELECT_PROPS()}
				isSearchable={false}
				// TODO: prevent change if loading
				onChange={(option) => {
					const event = createAnalyticsEvent({});
					fireUIAnalytics(event, 'button clicked', `${option!.value}View`);

					onViewChange(option!.value);
				}}
				options={getScheduleViewOptions(intlApi).map((viewOption) => viewOption)}
				spacing="compact"
				styles={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					control: (base) => ({ ...base, ...viewSelectStyles }),
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					...GET_EXTRA_SELECT_PROPS().styles,
				}}
				value={getScheduleViewOptions(intlApi).find(({ value }) => value === selectedView)}
				label={intlApi.formatMessage(scheduleCommonMessages.selectScheduleView, {
					scheduleName,
				})}
				placeholder={intlApi.formatMessage(scheduleCommonMessages.selectScheduleView, {
					scheduleName,
				})}
			/>
		</>
	);
};

export { DateNavigation };
